<template>
    <modal
        id="users-bulk-update"
        size="lg"
        :title="$t('usersBulkEdit.bulkEditing')"
        :okTitle="getOkTitle"
        :okDisabled="false"
        :ok-only="!canCurrentUserSaveBulkEditing"
        :ok-variant="getVariantColor"
        :cancelTitle="$t('modal.cancel')"
        :cancelDisabled="false"
        :disabled="false"
        :noCloseOnEsc="true"
        :noCloseOnBackdrop="true"
        isModalRoute
        @okHandler="onSubmit"
    >
        <b-container fluid v-if="canCurrentUserSaveBulkEditing">
            <b-row>
                <b-col>
                    <h5>
                        <b-badge class="p-2" variant="primary">
                            {{
                                $t('targetsBulkEdit.messages.youAreUpdating', {
                                    users: users.length,
                                })
                            }}
                        </b-badge>
                    </h5>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <div class="mb-2">
                        <small class="text-muted">
                            <i class="fa fa-angle-double-right fa-lg mr-2"></i>
                            <strong>
                                {{ $t('usersBulkEdit.messages.modalExplanation') }}
                            </strong>
                        </small>
                    </div>
                </b-col>
            </b-row>
            <b-row v-if="canChangeManager">
                <b-col>
                    <div class="d-inline-flex mb-2">
                        <b-form-checkbox
                            switch
                            v-model="$v.form.editManager.$model"
                            :disabled="false"
                            @change="$v.form.editManager.$model = $event"
                        >
                            <span> {{ $t('people.manager') }} </span>
                        </b-form-checkbox>
                    </div>
                    <b-collapse id="collapse-manager" v-model="$v.form.editManager.$model">
                        <c-users
                            :value.sync="$v.form.manager.$model"
                            :disabled="!$v.form.editManager.$model"
                            :state="managerState"
                        />
                    </b-collapse>
                </b-col>
            </b-row>
            <b-row v-if="canChangeOccupationGroups">
                <b-col>
                    <div class="d-inline-flex mb-2">
                        <b-form-checkbox
                            switch
                            v-model="$v.form.editOccupationGroups.$model"
                            :disabled="false"
                            @change="$v.form.editOccupationGroups.$model = $event"
                        >
                            {{ $t('form.label.occupationGroups') }}
                        </b-form-checkbox>
                    </div>
                    <b-collapse id="collapse-occupation-groups" v-model="$v.form.editOccupationGroups.$model">
                        <c-occupation-groups
                            :value.sync="$v.form.occupationGroups.$model"
                            :state="occupationGroupsState"
                            :showLabel="false"
                        />
                    </b-collapse>
                </b-col>
            </b-row>
            <b-row v-if="canChangeRole">
                <b-col>
                    <div class="d-inline-flex mb-2">
                        <b-form-checkbox
                            switch
                            v-model="$v.form.editRole.$model"
                            :disabled="false"
                            @change="$v.form.editRole.$model = $event"
                        >
                            {{ $t('roles.role') }}
                        </b-form-checkbox>
                    </div>
                    <b-collapse id="collapse-roles" v-model="$v.form.editRole.$model">
                        <c-roles :value.sync="$v.form.role.$model" :state="roleState" />
                    </b-collapse>
                </b-col>
            </b-row>
            <b-row v-if="canChangeModules">
                <b-col>
                    <div class="d-inline-flex mb-2">
                        <b-form-checkbox
                            switch
                            v-model="$v.form.editModules.$model"
                            :disabled="false"
                            @change="$v.form.editModules.$model = $event"
                        >
                            {{ $t('modules.modules') }}
                        </b-form-checkbox>
                    </div>
                    <b-collapse id="collapse-modules" v-model="$v.form.editModules.$model">
                        <c-modules
                            :value.sync="$v.form.modules.$model"
                            :showLabel="false"
                            :state="modulesState"
                            :editOption.sync="moduleEditOption"
                        />
                    </b-collapse>
                </b-col>
            </b-row>
            <b-row v-if="canEditNetwork">
                <b-col>
                    <div class="d-inline-flex mb-2">
                        <b-form-checkbox
                            switch
                            v-model="$v.form.editNetworks.$model"
                            :disabled="false"
                            @change="$v.form.editNetworks.$model = $event"
                        >
                            {{ $t('editNetworks') }}
                        </b-form-checkbox>
                    </div>
                    <b-collapse id="collapse-networks" v-model="$v.form.editNetworks.$model">
                        <c-networks
                            :value.sync="$v.form.networks.$model"
                            :showLabel="false"
                            :state="networksState"
                            :editOption.sync="networkEditOption"
                        />
                    </b-collapse>
                </b-col>
            </b-row>
            <b-row v-if="canEditAccessPermission">
                <b-col>
                    <div class="d-flex mb-2">
                        <b-form-checkbox
                            switch
                            v-model="$v.form.editPermissions.$model"
                            :disabled="false"
                            @change="$v.form.editPermissions.$model = $event"
                        >
                            {{ $t('usersBulkEdit.accessPermission') }}
                        </b-form-checkbox>
                    </div>
                    <b-collapse id="collapse-access-permission" class="m-2" v-model="$v.form.editPermissions.$model">
                        <c-enable-permissions
                            :value.sync="$v.form.accessPermissions.$model"
                            :showLabel="false"
                            :state="permissionsState"
                            :editOption.sync="permissionEditOption"
                        />
                    </b-collapse>
                </b-col>
            </b-row>
            <b-row v-if="canEditBonification">
                <b-col>
                    <div class="d-flex mb-2">
                        <b-form-checkbox
                            switch
                            v-model="$v.form.editBonification.$model"
                            :disabled="false"
                            @change="$v.form.editBonification.$model = $event"
                        >
                            {{ $t('usersBulkEdit.bonification') }}
                        </b-form-checkbox>
                    </div>
                    <b-collapse id="collapse-bonification" class="m-2" v-model="$v.form.editBonification.$model">
                        <b-form-checkbox
                            v-model="$v.form.bonification.$model"
                            :disabled="false"
                            :state="bonificationState"
                            @change="$v.form.bonification.$model = $event"
                        >
                            {{
                                $v.form.bonification.$model
                                    ? $t('usersBulkEdit.activatedBonification')
                                    : $t('usersBulkEdit.deactivatedBonification')
                            }}
                        </b-form-checkbox>
                    </b-collapse>
                </b-col>
            </b-row>
            <b-row v-if="canEditNBoxVisualization">
                <b-col>
                    <div class="d-flex mb-2">
                        <b-form-checkbox
                            switch
                            v-model="$v.form.editNBox.$model"
                            :disabled="false"
                            @change="$v.form.editNBox.$model = $event"
                        >
                            {{ $t('usersBulkEdit.nBox') }}
                        </b-form-checkbox>
                    </div>
                    <b-collapse id="collapse-nBox" class="m-2" v-model="$v.form.editNBox.$model">
                        <b-form-checkbox
                            v-model="$v.form.eligibleNBox.$model"
                            :disabled="false"
                            :state="nBoxState"
                            @change="changeNBoxVisualization($event)"
                        >
                            {{ nBoxText }}
                        </b-form-checkbox>
                    </b-collapse>
                </b-col>
            </b-row>
            <b-row v-if="canEditUserStatus">
                <b-col>
                    <div class="d-flex mb-2">
                        <b-form-checkbox switch v-model="$v.form.editStatus.$model" :disabled="false">
                            {{ $t('people.status') }}
                        </b-form-checkbox>
                    </div>
                    <b-collapse id="collapse-users-status" class="m-2" v-model="$v.form.editStatus.$model">
                        <Status
                            :state="statusState"
                            :selected.sync="$v.form.status.$model"
                            :multiple="false"
                            :except="'inactive'"
                        />
                    </b-collapse>
                </b-col>
            </b-row>
        </b-container>
        <b-container v-else>
            <b-alert show variant="danger">
                {{ $t('usersBulkEdit.messages.userNotAllowed') }}
            </b-alert>
        </b-container>
    </modal>
</template>

<script>
import i18n from '@/i18n'
import Swal from 'sweetalert2'
import Modal from '@/views/components/modal'
import {
    Status,
    Roles as CRoles,
    Users as CUsers,
    Modules as CModules,
    Networks as CNetworks,
    OccupationGroups as COccupationGroups,
    EnablePermissions as CEnablePermissions,
} from '@/views/components/fields'

import api from '@/services/api'
import { validationMixin } from 'vuelidate'
import { requiredIf, minLength } from 'vuelidate/lib/validators'
import { userPermissionsMixin } from '@/mixins'
import initialValues from './initial-values'

export default {
    name: 'UsersBulkEditModal',
    components: {
        Modal,
        COccupationGroups,
        CRoles,
        CUsers,
        CModules,
        CNetworks,
        CEnablePermissions,
        Status,
    },
    props: ['users'],
    mixins: [validationMixin, userPermissionsMixin],
    validations: {
        form: {
            bonification: {
                required: requiredIf((nestedModel) => nestedModel.editBonification),
            },
            eligibleNBox: {
                required: requiredIf((nestedModel) => nestedModel.editNBox),
            },
            status: {
                required: requiredIf((nestedModel) => nestedModel.editStatus),
            },
            occupationGroups: {},
            role: {
                required: requiredIf((nestedModel) => nestedModel.editRole),
            },
            manager: {},
            modules: {
                required: requiredIf((nestedModel) => nestedModel.editModules),
                minLength: minLength(1),
            },
            networks: {
                required: requiredIf((nestedModel) => nestedModel.editNetworks),
            },
            accessPermissions: {
                required: requiredIf((nestedModel) => nestedModel.editPermissions),
                minLength: minLength(1),
            },
            editModules: {},
            editPermissions: {},
            editOccupationGroups: {},
            editBonification: {},
            editRole: {},
            editManager: {},
            editNetworks: {},
            editNBox: {},
            editStatus: {},
        },
    },
    data() {
        return {
            form: { ...initialValues },
            moduleEditOption: {},
            networkEditOption: {},
            permissionEditOption: {},
            nBoxText: this.$t('usersBulkEdit.releaseNBox'),
            isEditingManager: false,
        }
    },
    methods: {
        onSubmit(event) {
            event.preventDefault()
            if (!this.canCurrentUserSaveBulkEditing) {
                return this.$router.back(-1)
            }
            if (!this.users.length) {
                return this.swalNoUserSelected()
            }
            if (!this.getEditedFields().length) {
                return this.swalNoChanges()
            }
            if (!this.validate()) {
                return
            }

            const htmlMessage = this.getHtmlEditedFields()
            Swal.queue([
                {
                    type: 'warning',
                    title: i18n.t('notification.areYouSure'),
                    html: htmlMessage,
                    confirmButtonText: i18n.t('notification.yes'),
                    cancelButtonText: i18n.t('notification.no'),
                    showLoaderOnConfirm: true,
                    showCancelButton: true,
                    preConfirm: () => {
                        const params = this.generateParams()

                        return api
                            .post('users/bulk-edit', params)
                            .then(() => {
                                this.updateUsersNetworks(params.users)
                                Swal.insertQueueStep({
                                    type: 'success',
                                    title: i18n.t('notification.requestSentSuccessfully'),
                                    text: i18n.t('notification.usersBulkEditRequestSuccessfullySent'),
                                })
                                this.$emit('updated')
                                this.$router.back(-1)
                            })
                            .catch((error) => {
                                Swal.insertQueueStep({
                                    title: i18n.t('notification.somethingWentWrong'),
                                    type: 'error',
                                    text: i18n.t('notification.usersBulkEditRequestFailed'),
                                })
                                console.log(error)
                            })
                    },
                },
            ])
        },
        generateParams() {
            const editedFields = this.getEditedFields()

            let params = {
                fields: {},
                users: this.users,
            }

            editedFields.forEach((field) => {
                let permissions = {}

                switch (field) {
                    case 'editOccupationGroups':
                        params.fields.occupationGroups = this.$v.form.occupationGroups.$model.map((occupationGroup) => {
                            return occupationGroup.value
                        })
                        break
                    case 'editRole':
                        params.fields.role = this.mapperToApi(this.$v.form.role.$model)
                        break
                    case 'editManager':
                        params.fields.manager = this.mapperToApi(this.$v.form.manager.$model)
                        this.isEditingManager = true
                        break
                    case 'editModules':
                        params.fields.modules = this.form.modules.reduce((modules, module) => {
                            return {
                                ...modules,
                                [module.split(' ').join('_')]: this.moduleEditOption.value,
                            }
                        }, {})
                        break
                    case 'editBonification':
                        params.fields.bonification = this.$v.form.bonification.$model
                        break
                    case 'editPermissions':
                        this.$v.form.accessPermissions.$model.forEach((element) => {
                            permissions[element] = this.permissionEditOption.value
                        })
                        params.fields.accessPermissions = permissions
                        break
                    case 'editNetworks':
                        params.fields.networks = {
                            types: this.$v.form.networks.$model,
                            enable: this.networkEditOption.value,
                        }
                        break
                    case 'editNBox':
                        params.fields.eligibleNBox = this.$v.form.eligibleNBox.$model
                        break
                    case 'editStatus':
                        params.fields.status = this.$v.form.status.$model.length
                            ? this.$v.form.status.$model.reduce((status) => status).value
                            : this.$v.form.status.$model.value
                        break
                }
            })

            return params
        },
        mapperToApi(field) {
            return field.value
        },
        validate() {
            this.$v.$touch()
            return this.formIsValid
        },
        getEditedFields() {
            return Object.keys(this.form).filter((key) => {
                return key.includes('edit') && this.form[key] == true
            })
        },
        swalNoUserSelected() {
            Swal({
                type: 'warning',
                title: i18n.t('people.noUserSelected'),
                cancelButtonText: i18n.t(`modal.close`),
                showCancelButton: true,
                showConfirmButton: false,
            }).then(() => this.$router.back())
        },
        swalNoChanges() {
            Swal({
                type: 'warning',
                title: i18n.t('usersBulkEdit.messages.noChanges'),
                text: i18n.t('usersBulkEdit.messages.selectFields'),
                cancelButtonText: i18n.t(`modal.close`),
                showCancelButton: true,
                showConfirmButton: false,
            })
        },
        getHtmlEditedFields() {
            const editedFields = this.getEditedFields()

            let div = document.createElement('div')
            let list = document.createElement('ul')
            let h5 = document.createElement('h5')
            let h5Text = document.createTextNode(i18n.t('usersBulkEdit.messages.updatedFields') + ':')

            list.className = 'list-group'

            editedFields.forEach((field) => {
                let item = document.createElement('li')
                let content = document.createTextNode(this.getFieldTranslate(field))

                item.appendChild(content)
                item.className = 'd-flex list-group-item justify-content-center'
                list.appendChild(item)
            })

            h5.appendChild(h5Text)
            div.appendChild(h5)
            div.appendChild(list)

            return div
        },
        getFieldTranslate(field) {
            const fields = {
                editOccupationGroups: i18n.t('form.label.occupationGroups'),
                editRole: i18n.t('roles.role'),
                editManager: i18n.t('people.manager'),
                editModules: i18n.t('modules.modules'),
                editPermissions: i18n.t('usersBulkEdit.accessPermission'),
                editBonification: i18n.t('usersBulkEdit.bonification'),
                editNetworks: i18n.t('editNetworks'),
                editNBox: i18n.t('usersBulkEdit.nBox'),
                editStatus: i18n.t('people.status'),
            }

            return fields[field]
        },

        changeNBoxVisualization($event) {
            this.$v.form.eligibleNBox.$model = $event
            this.nBoxText = $event ? this.$t('usersBulkEdit.enableNBox') : this.$t('usersBulkEdit.disableNBox')
        },
        updateUsersNetworks(users) {
            if (this.isEditingManager) {
                return api.post('users/bulk/update-users-network', {
                    users: users,
                    update_leader: true,
                    update_team: false,
                    update_peers: false,
                })
            }
        },
    },
    computed: {
        currentUser() {
            return this.$store.state.currentUser.user
        },
        currentUserHasApiModule() {
            return this.currentUser.company.url_prefix != null
        },
        canCurrentUserSaveBulkEditing() {
            const availableModules = []
            const availableRoles = [
                'god',
                'administrator',
                'local_administrator',
                'is_target_master',
                'is_values_master',
            ]
            return this.userHasPermission(this.currentUser, availableRoles, availableModules)
        },
        canChangeRole() {
            const availableModules = []
            const availableRoles = ['god', 'administrator']
            return this.userHasPermission(this.currentUser, availableRoles, availableModules)
        },
        canChangeManager() {
            const availableModules = []
            const availableRoles = [
                'god',
                'administrator',
                'local_administrator',
                'is_values_master',
                'is_target_master',
            ]
            return this.userHasPermission(this.currentUser, availableRoles, availableModules)
        },
        canChangeOccupationGroups() {
            const availableModules = []
            const availableRoles = ['god', 'administrator', 'local_administrator', 'is_values_master']
            return this.userHasPermission(this.currentUser, availableRoles, availableModules)
        },
        canChangeModules() {
            const availableModules = []
            const availableRoles = [
                'god',
                'administrator',
                'local_administrator',
                'is_values_master',
                'is_target_master',
            ]
            return this.userHasPermission(this.currentUser, availableRoles, availableModules)
        },
        canEditNetwork() {
            const availableModules = ['has_value']
            const availableRoles = ['god', 'administrator', 'local_administrator', 'is_values_master']
            return this.userHasPermission(this.currentUser, availableRoles, availableModules)
        },
        canEditAccessPermission() {
            const availableModules = []
            const availableRoles = ['god', 'administrator', 'local_administrator']
            return this.userHasPermission(this.currentUser, availableRoles, availableModules)
        },
        canEditBonification() {
            const availableModules = ['has_goal', 'has_kpi', 'has_value']
            const availableRoles = [
                'god',
                'administrator',
                'local_administrator',
                'is_values_master',
                'is_target_master',
            ]
            return this.userHasPermission(this.currentUser, availableRoles, availableModules)
        },
        canEditNBoxVisualization() {
            const availableModules = ['has_goal', 'has_kpi', 'has_value']
            const availableRoles = [
                'god',
                'administrator',
                'local_administrator',
                'is_target_master',
                'is_values_master',
            ]
            return this.userHasPermission(this.currentUser, availableRoles, availableModules)
        },
        canEditUserStatus() {
            const availableModules = []
            const availableRoles = [
                'god',
                'administrator',
                'local_administrator',
                'is_values_master',
                'is_target_master',
            ]
            return this.userHasPermission(this.currentUser, availableRoles, availableModules)
        },
        getOkTitle() {
            return this.canCurrentUserSaveBulkEditing ? i18n.t('modal.save') : i18n.t('modal.close')
        },
        getVariantColor() {
            return this.canCurrentUserSaveBulkEditing ? 'primary' : 'light'
        },
        formIsValid() {
            return !this.$v.form.$anyError
        },
        occupationGroupsState() {
            return !this.$v.form.occupationGroups.$dirty ? null : true
        },
        roleState() {
            return !this.$v.form.role.$dirty ? null : !this.$v.form.role.$invalid
        },
        managerState() {
            return !this.$v.form.manager.$dirty ? null : !this.$v.form.manager.$invalid
        },
        modulesState() {
            return !this.$v.form.modules.$dirty ? null : !this.$v.form.modules.$invalid
        },
        permissionsState() {
            return !this.$v.form.accessPermissions.$dirty ? null : !this.$v.form.accessPermissions.$invalid
        },
        bonificationState() {
            return !this.$v.form.bonification.$dirty ? null : !this.$v.form.bonification.$invalid
        },
        nBoxState() {
            if (this.nBoxText != this.$t('usersBulkEdit.releaseNBox')) {
                return this.nBoxText == this.$t('usersBulkEdit.enableNBox')
                    ? this.$v.form.eligibleNBox.$dirty
                    : this.$v.form.eligibleNBox.$invalid
            }
            return null
        },
        statusState() {
            return !this.$v.form.status.$dirty ? null : !this.$v.form.status.$invalid
        },
        networksState() {
            return !this.$v.form.networks.$dirty ? null : !this.$v.form.networks.$invalid
        },
    },
}
</script>
